import React, { useEffect, useState } from "react";
import { Printer, Move, RotateCcw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PRINT_SIZES, ADJUST_DEFAULT, getPrintSize, setPrintSize, getPrintAdjust, setPrintAdjust, getPrintColors, setPrintColors } from "../lib/print";

// Current print size; updates when it is changed anywhere on the page.
export function usePrintSize() {
  const [size, setSize] = useState(getPrintSize);
  useEffect(() => {
    const onChange = (e) => setSize(e.detail);
    window.addEventListener("pd-print-size", onChange);
    return () => window.removeEventListener("pd-print-size", onChange);
  }, []);
  return size;
}

// Current print colours ("light" for paper, or "full"); the ticket container gets the matching class.
export function usePrintColors() {
  const [v, setV] = useState(getPrintColors);
  useEffect(() => {
    const onChange = (e) => setV(e.detail);
    window.addEventListener("pd-print-colors", onChange);
    return () => window.removeEventListener("pd-print-colors", onChange);
  }, []);
  return v;
}

const NumBox = ({ label, hint, value, onChange, step, min, max, testid }) => (
  <label className="inline-flex items-center gap-1" title={hint}>
    <span>{label}</span>
    <input type="number" value={value} step={step} min={min} max={max} onChange={(e) => onChange(e.target.value)}
      className="h-7 w-16 rounded-md border border-[#E5D9C5] bg-white px-1.5 text-xs" data-testid={testid} />
  </label>
);

// Per-PC choice of paper for the 6x4 in Protocol / Saree cards (remembered in this browser window),
// plus a position adjustment so the print starts exactly where the printer's paper starts.
export default function PrintSizeSelect({ className = "" }) {
  const size = usePrintSize();
  const colors = usePrintColors();
  const [adj, setAdj] = useState(getPrintAdjust);
  const [open, setOpen] = useState(false);
  const update = (k, v) => { const a = { ...adj, [k]: v }; setAdj(a); setPrintAdjust(a); };
  const changed = Number(adj.x) || Number(adj.y) || Number(adj.s) !== ADJUST_DEFAULT.s;
  const { user } = useAuth();
  // Print settings are set up by the Super Admin / Temple Admin on each PC; counter staff just print with them.
  if (!["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user?.role)) return null;
  return (
    <div className={`hidden md:inline-flex flex-col items-end gap-1.5 text-xs text-slate-600 ${className}`}>
      <div className="inline-flex flex-wrap items-center gap-2" data-testid="print-size">
        <Printer className="w-4 h-4 text-maroon-700" />
        <span className="font-semibold">Print size:</span>
        <select value={size} onChange={(e) => setPrintSize(e.target.value)}
          className="h-8 rounded-md border border-[#E5D9C5] bg-white px-2 text-xs" data-testid="print-size-select">
          {PRINT_SIZES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {size !== "slip" && (
          <select value={colors} onChange={(e) => setPrintColors(e.target.value)} title="Print colours"
            className="h-8 rounded-md border border-[#E5D9C5] bg-white px-2 text-xs" data-testid="print-colors-select">
            <option value="light">Colours: Light - for paper (clear print)</option>
            <option value="full">Colours: Full colour (as designed)</option>
          </select>
        )}
        {size !== "slip" && (
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="print-adjust-btn"
            className={`h-8 px-2 rounded-md border inline-flex items-center gap-1 ${changed ? "border-maroon-600 text-maroon-800 bg-gold-50" : "border-[#E5D9C5] bg-white"}`}>
            <Move className="w-3.5 h-3.5" /> Adjust position
          </button>
        )}
      </div>
      {open && size !== "slip" && (
        <div className="flex flex-wrap items-center justify-end gap-3 rounded-lg border border-[#E5D9C5] bg-white px-3 py-2" data-testid="print-adjust">
          <NumBox label="Down (mm)" hint="Move the print down; use a minus value (e.g. -5) to move it up" value={adj.y} step="0.5" min="-30" max="30" onChange={(v) => update("y", v)} testid="print-adjust-y" />
          <NumBox label="Right (mm)" hint="Move the print right; use a minus value to move it left" value={adj.x} step="0.5" min="-30" max="30" onChange={(v) => update("x", v)} testid="print-adjust-x" />
          <NumBox label="Size (%)" hint="Shrink or enlarge the card" value={adj.s} step="1" min="70" max="110" onChange={(v) => update("s", v)} testid="print-adjust-s" />
          <button type="button" onClick={() => { setAdj({ ...ADJUST_DEFAULT }); setPrintAdjust({ ...ADJUST_DEFAULT }); }}
            className="h-7 px-2 rounded-md border border-[#E5D9C5] inline-flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
          <p className="w-full text-[11px] text-slate-500 text-right">Print once, see where the card starts on the paper, then move it (minus = up / left). Saved on this PC.</p>
        </div>
      )}
    </div>
  );
}
